// Pure text-normalization helpers for voice mode TTS.
//
// These are self-contained, side-effect-free functions extracted from
// useVoiceMode.ts. They have zero React/closure dependencies and exist to
// make TTS pronunciation deterministic (symbol/abbreviation expansion,
// markdown stripping, length capping). Kept in a sibling file so the hook
// orchestrator stays focused on the SpeechRecognition / TTS state machine.

export const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** Returns "1st", "2nd", "3rd", "4th", ... "21st", "22nd", "23rd", etc. */
export function ordinal(n: number): string {
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
}

/**
 * Capitalize-preserving replace helper. If the matched text starts with an
 * uppercase letter (sentence-initial), the replacement is also capitalized.
 */
export function caseAwareReplace(text: string, pattern: RegExp, replacement: string): string {
  return text.replace(pattern, (match) => {
    const isUpper = /^[A-Z]/.test(match);
    if (!isUpper) return replacement;
    return replacement[0].toUpperCase() + replacement.slice(1);
  });
}

/**
 * Expand symbols and common abbreviations into their natural spoken forms
 * before sending to TTS. ElevenLabs' built-in expansion is inconsistent —
 * it sometimes reads "°F" as "degrees Fahrenheit" and sometimes as "F" or
 * silently. Doing it ourselves makes pronunciation deterministic.
 *
 * Order matters:
 *   1. Date/time/phone normalizers run FIRST so digit-heavy patterns aren't
 *      partially eaten by the more permissive currency/range/paren rules.
 *   2. Unit-suffixed symbols (°F, °C, lb-ft) are matched before bare
 *      fallbacks (°, lb).
 *   3. Parenthetical handler runs LAST so its contents have already been
 *      expanded.
 */
export function expandSpokenSymbols(text: string): string {
  let out = text;

  // 0. Strip thousands-separator commas from numbers BEFORE any other rule.
  // ElevenLabs reads "99,980" as "ninety-nine [pause] nine eighty" because
  // it treats the comma as a clause break, not a numeric separator. With
  // the commas removed ("99980") it reads it as a single number:
  // "ninety-nine thousand nine hundred eighty". Safe ordering: ISO dates
  // use hyphens (not commas), phone parens are matched separately below,
  // and free-floating commas in prose lack the \d{3} grouping required
  // here, so this only fires on real numeric groupings.
  out = out.replace(/(\d{1,3}(?:,\d{3})+(?:\.\d+)?)/g, (m) => m.replace(/,/g, ''));

  // 1. ISO dates: "2026-04-28" → "April 28th, 2026"
  out = out.replace(/\b(\d{4})-(\d{2})-(\d{2})\b/g, (m, y, mo, d) => {
    const monthIdx = parseInt(mo, 10) - 1;
    const day = parseInt(d, 10);
    if (monthIdx < 0 || monthIdx > 11 || day < 1 || day > 31) return m;
    return `${MONTHS[monthIdx]} ${ordinal(day)}, ${y}`;
  });

  // 2. Phone numbers in parens: "(555) 123-4567" → "555 123 4567"
  // Done BEFORE the paren handler so it doesn't mangle area codes into "or 555".
  // Spaces between groups read naturally as digit groups in TTS.
  out = out.replace(/\((\d{3})\)\s*(\d{3})[-.\s]?(\d{4})\b/g, '$1 $2 $3');

  // 3. Time-of-day: ensure space before am/pm so "10:30am" doesn't read funny.
  out = out.replace(/(\d{1,2}:\d{2})\s*([ap]\.?m\.?)\b/gi, '$1 $2');

  // 4. Temperature
  out = out
    .replace(/°\s*F\b/g, ' degrees Fahrenheit')
    .replace(/°\s*C\b/g, ' degrees Celsius')
    .replace(/°/g, ' degrees');

  // 5. Currency — step 0 has already stripped thousands separators, so the
  // amount is always bare digits with optional decimal at this point.
  out = out
    .replace(/\$\s*(\d+(?:\.\d+)?)/g, '$1 dollars')
    .replace(/€\s*(\d+(?:\.\d+)?)/g, '$1 euros')
    .replace(/£\s*(\d+(?:\.\d+)?)/g, '$1 pounds')
    .replace(/¥\s*(\d+(?:\.\d+)?)/g, '$1 yen');

  // 6. Number sign (e.g., "#5" → "number 5")
  out = out.replace(/#\s*(\d+)/g, 'number $1');

  // 7. Percent
  out = out.replace(/(\d+(?:\.\d+)?)\s*%/g, '$1 percent');

  // 8. Speed
  out = out
    .replace(/(\d+(?:\.\d+)?)\s*mph\b/gi, '$1 miles per hour')
    .replace(/(\d+(?:\.\d+)?)\s*km\/h\b/gi, '$1 kilometers per hour')
    .replace(/(\d+(?:\.\d+)?)\s*kph\b/gi, '$1 kilometers per hour');

  // 9. Torque — must come BEFORE the bare lb rule so "350 lb-ft" is
  // converted to "350 foot-pounds" instead of "350 pounds-ft".
  out = out.replace(/(\d+(?:\.\d+)?)\s*lb[-·]ft\b/gi, '$1 foot-pounds');

  // 10. Common unit abbreviations (require a number prefix to avoid eating
  // English words: "ft" without a number could be a name fragment)
  out = out
    .replace(/(\d+(?:\.\d+)?)\s*ft\b/gi, '$1 feet')
    .replace(/(\d+(?:\.\d+)?)\s*lbs?\b/gi, '$1 pounds')
    .replace(/(\d+(?:\.\d+)?)\s*oz\b/gi, '$1 ounces')
    .replace(/(\d+(?:\.\d+)?)\s*kg\b/gi, '$1 kilograms');

  // 11. Number ranges: "5-10 minutes" → "5 to 10 minutes". Constrained to
  // digit-dash-digit so compound words ("south-southeast", "5-year-old")
  // are unaffected. ISO dates and phone numbers already converted above.
  out = out.replace(/(\d+)\s*[-–]\s*(\d+)/g, '$1 to $2');

  // 12. Word-symbol replacements (only when surrounded by whitespace so we
  // don't break things like "AT&T")
  out = out
    .replace(/\s&\s/g, ' and ')
    .replace(/\s@\s/g, ' at ');

  // 13. Common Latin and English abbreviations — case-aware so a sentence-
  // initial "Etc." or "Vs." doesn't drop its capital.
  out = caseAwareReplace(out, /\bvs\.?/gi, 'versus');
  out = caseAwareReplace(out, /\be\.g\.,?/gi, 'for example,');
  out = caseAwareReplace(out, /\bi\.e\.,?/gi, 'that is,');
  out = caseAwareReplace(out, /\betc\./gi, 'et cetera');
  // Common business / scheduling abbreviations
  out = out.replace(/\bN\/A\b/g, 'not applicable');
  out = caseAwareReplace(out, /\bASAP\b/g, 'as soon as possible');
  out = out.replace(/\bTBD\b/g, 'to be determined');

  // 14. Parenthetical handler — applied AFTER symbol expansion so the
  // content inside reflects the natural spoken form. If the contents
  // start with a digit, it's almost always an alternative measurement
  // ("(17 degrees Celsius)") and reads better as "or X". Otherwise
  // treat it as a normal aside with comma intonation.
  out = out.replace(/\s*\(([^)]+)\)/g, (_, content) => {
    const trimmed = content.trim();
    // Skip pure-digit content (likely a remaining phone fragment, ID, or
    // year-only that the upstream rules didn't catch) — keep as-is so
    // it doesn't become a misleading "or 555".
    if (/^\d+$/.test(trimmed)) return ` ${trimmed}`;
    return /^\d/.test(trimmed) ? `, or ${trimmed},` : `, ${trimmed},`;
  });

  // 15. Tidy whitespace and punctuation
  out = out
    .replace(/\s+/g, ' ')
    .replace(/\s+([,.!?])/g, '$1')
    // Strip trailing commas before sentence terminators (from paren closers
    // landing right before a period).
    .replace(/,\s*([.!?])/g, '$1')
    // Strip a trailing comma at end-of-string (paren closer with no
    // following terminator).
    .replace(/,\s*$/, '')
    .trim();

  return out;
}

/**
 * Clean text for speech — strip markdown, expand symbols/abbreviations,
 * preserve full content up to a generous limit.
 * Cap at ~3500 chars to avoid ElevenLabs API issues / long buffering delays.
 * This is NOT an aggressive truncation — 3500 chars is ~2+ minutes of speech.
 */
export function cleanForSpeech(text: string, maxChars = 3500): string {
  const stripped = text
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/#{1,6}\s+/g, '')
    .replace(/[-*]\s+/g, '')
    .replace(/`{1,3}[^`]*`{1,3}/g, (m) => m.replace(/`/g, '')) // strip backticks from inline code
    .replace(/\*{1,2}/g, '')   // strip orphaned asterisks (markdown split across sentences)
    .replace(/`+/g, '')        // strip orphaned backticks
    .replace(/\n+/g, ' ')
    .trim();

  const clean = expandSpokenSymbols(stripped);

  if (clean.length <= maxChars) return clean;

  // Try to break at a sentence boundary near the limit
  const truncated = clean.slice(0, maxChars);
  const lastSentenceEnd = Math.max(
    truncated.lastIndexOf('.'),
    truncated.lastIndexOf('!'),
    truncated.lastIndexOf('?'),
  );
  return lastSentenceEnd > maxChars * 0.5
    ? truncated.slice(0, lastSentenceEnd + 1).trim()
    : truncated.trim();
}
